import { useEffect, useState, type FormEvent } from 'react';
import {
  api,
  BladApi,
  type Kategoria,
  type KartaMontazu,
  type RodzajPodpisu,
  type Sesja,
} from '../api';
import { data, dataICzas } from '../format';
import { useIdOperacji } from '../operacje';
import { idz } from '../router';
import { FormularzMontazu } from '../components/FormularzMontazu';
import { IkonaPodpis, IkonaWstecz } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import {
  ChipMontazu,
  Dana,
  Karta,
  Komunikat,
  PoleTekstowe,
  Przycisk,
  PrzyciskMarki,
  Tag,
} from '../components/ui';

const PODPISY: { kod: RodzajPodpisu; nazwa: string; opis: string }[] = [
  { kod: 'wydal', nazwa: 'Wydał', opis: 'wydanie podzespołów z magazynu' },
  { kod: 'wykonal', nazwa: 'Wykonał', opis: 'złożenie i uruchomienie maszyny' },
  { kod: 'sprawdzil', nazwa: 'Sprawdził', opis: 'kontrola przed wysyłką' },
];

/**
 * Karta zlecenia montażu — układ druku „Zlecenie montażu v1.1": zamówienie u góry,
 * wykaz podzespołów w środku, trzy podpisy na końcu.
 *
 * Podpis to kliknięcie przycisku, a status wylicza się z podpisów — nigdzie nie ma
 * listy „zmień status" (CLAUDE.md §1 zasada 3). Przycisk zakładający kartę urządzenia
 * jest nieaktywny do trzeciego podpisu: kontrola jakości wynika z układu ekranu.
 */
export function AssemblyOrderView({ id, sesja }: { id: string; sesja: Sesja }) {
  const mozePodpisywac = sesja.permissions.includes('assembly.sign');
  const mozeZakladacKarty = sesja.permissions.includes('device.write');
  const mozeEdytowac = sesja.permissions.includes('assembly.write');

  const [karta, setKarta] = useState<KartaMontazu | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [bladAkcji, setBladAkcji] = useState<string | null>(null);
  const [zajete, setZajete] = useState<RodzajPodpisu | null>(null);
  const [konczenie, setKonczenie] = useState(false);
  const [edycja, setEdycja] = useState(false);

  useEffect(() => {
    let aktualne = true;
    setKarta(null);
    setKonczenie(false);
    setEdycja(false);

    api
      .montaz(id)
      .then((wynik) => aktualne && setKarta(wynik))
      .catch((error: unknown) => {
        if (aktualne) {
          setBlad(error instanceof BladApi ? error.message : 'Nie udało się wczytać zlecenia.');
        }
      });

    return () => {
      aktualne = false;
    };
  }, [id]);

  async function przelaczPodpis(rodzaj: RodzajPodpisu, zlozony: boolean): Promise<void> {
    setZajete(rodzaj);
    setBladAkcji(null);
    try {
      setKarta(
        zlozony
          ? await api.cofnijPodpisMontazu(id, rodzaj)
          : await api.podpiszMontaz(id, rodzaj),
      );
    } catch (error: unknown) {
      setBladAkcji(error instanceof BladApi ? error.message : 'Nie udało się zapisać podpisu.');
    } finally {
      setZajete(null);
    }
  }

  if (blad) {
    return (
      <>
        <NaglowekWidoku tytul="Zlecenie montażu" />
        <Komunikat tresc={blad} />
        <div className="mt-14">
          <Przycisk
            type="button"
            onClick={() => idz('/montaze')}
            ikona={<IkonaWstecz rozmiar={16} />}
          >
            Wróć do listy
          </Przycisk>
        </div>
      </>
    );
  }

  if (!karta) {
    return (
      <>
        <NaglowekWidoku tytul="Zlecenie montażu" />
        <p className="t-small text-n-500">Wczytywanie…</p>
      </>
    );
  }

  const { montaz, podpisy, mozeZakonczyc } = karta;
  const urzadzenie = montaz.devices[0];

  return (
    <>
      <NaglowekWidoku
        tytul={`Montaż ${montaz.numer}`}
        podtytul={`${montaz.customer.nazwa} · ${montaz.rodzaj === 'NOWA' ? 'maszyna nowa' : 'maszyna używana'}`}
        akcje={
          <>
            <Przycisk
              type="button"
              onClick={() => idz('/montaze')}
              ikona={<IkonaWstecz rozmiar={16} />}
            >
              Lista
            </Przycisk>
            {/* Jedna akcja główna w widoku (CLAUDE.md §5): dopóki brakuje podpisów,
                najważniejsze jest uzupełnienie druku; po komplecie — założenie karty. */}
            {mozeEdytowac && !urzadzenie && !edycja && !konczenie && (
              mozeZakonczyc && mozeZakladacKarty ? (
                <Przycisk type="button" onClick={() => setEdycja(true)}>
                  Uzupełnij zlecenie
                </Przycisk>
              ) : (
                <PrzyciskMarki type="button" onClick={() => setEdycja(true)}>
                  Uzupełnij zlecenie
                </PrzyciskMarki>
              )
            )}
            {mozeZakonczyc && mozeZakladacKarty && !konczenie && !edycja && (
              <PrzyciskMarki type="button" onClick={() => setKonczenie(true)}>
                Utwórz kartę urządzenia
              </PrzyciskMarki>
            )}
          </>
        }
      />

      {bladAkcji && (
        <div className="mb-14">
          <Komunikat tresc={bladAkcji} />
        </div>
      )}

      {konczenie && (
        <FormularzZakonczenia
          karta={karta}
          onGotowe={(nowa) => {
            setKarta(nowa);
            setKonczenie(false);
          }}
          onAnuluj={() => setKonczenie(false)}
        />
      )}

      <div className="grid gap-14 xl:grid-cols-[1fr_340px]">
        <div className="min-w-0">
          {edycja ? (
            <FormularzMontazu
              karta={karta}
              onZapisano={(nowa) => {
                setKarta(nowa);
                setEdycja(false);
              }}
              onAnuluj={() => setEdycja(false)}
            />
          ) : (
            <>
          <Karta tytul="Zamówienie" akcje={<ChipMontazu status={montaz.status} />}>
            <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
              <Dana etykieta="Numer zlecenia" mono>
                {montaz.numer}
              </Dana>
              <Dana etykieta="Numer zamówienia" mono>
                {montaz.numerZamowienia ?? ''}
              </Dana>
              <Dana etykieta="Klient">{montaz.customer.nazwa}</Dana>
              <Dana etykieta="Zlecający">{montaz.zlecajacy.imieNazwisko}</Dana>
              <Dana etykieta="Data zlecenia">{data(montaz.dataZlecenia)}</Dana>
              <Dana etykieta="Data realizacji">{data(montaz.dataRealizacji)}</Dana>
              <Dana etykieta="Dokument">
                {[montaz.typDokumentu, montaz.numerDokumentu].filter(Boolean).join(' ')}
              </Dana>
            </dl>
          </Karta>

          <Karta
            tytul="Wykaz podzespołów"
            akcje={<span className="t-small text-n-500">przechodzą na kartę urządzenia</span>}
          >
            <dl className="grid grid-cols-[auto_1fr] gap-x-22 gap-y-10">
              <Dana etykieta="Producent">{montaz.producent ?? ''}</Dana>
              <Dana etykieta="Model głowicy">{montaz.modelGlowicy ?? ''}</Dana>
              <Dana etykieta="Model napędu">{montaz.modelNapedu ?? ''}</Dana>
              <Dana etykieta="Podstawa">{montaz.podstawa ?? ''}</Dana>
              <Dana etykieta="Blat">{montaz.blat ?? ''}</Dana>
              <Dana etykieta="Numer fabryczny" mono>
                {montaz.serialNumber ?? ''}
              </Dana>
            </dl>
            {montaz.inne.length > 0 && (
              <div className="mt-13 flex flex-wrap gap-5">
                {montaz.inne.map((pozycja) => (
                  <Tag key={pozycja}>{pozycja}</Tag>
                ))}
              </div>
            )}
          </Karta>

          {montaz.czesci.length > 0 && (
            <Karta tytul={`Zużyte części · ${montaz.czesci.length}`} wypelnienie={false}>
              <div className="overflow-x-auto">
                <table className="tab">
                  <thead>
                    <tr>
                      <th>Numer części</th>
                      <th>Opis</th>
                      <th>Ilość</th>
                    </tr>
                  </thead>
                  <tbody>
                    {montaz.czesci.map((pozycja) => (
                      <tr key={pozycja.id}>
                        <td className="t-mono-data text-n-600">{pozycja.numerCzesci ?? '—'}</td>
                        <td className="t-body text-n-900">{pozycja.opis}</td>
                        <td className="t-mono-data text-n-700">{String(pozycja.ilosc)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Karta>
          )}

          {(montaz.uwagi || montaz.uwagiWykonania) && (
            <Karta tytul="Uwagi">
              {montaz.uwagi && (
                <p className="t-body mb-10 text-n-900">
                  <span className="t-label block text-n-600">Do zlecenia</span>
                  {montaz.uwagi}
                </p>
              )}
              {montaz.uwagiWykonania && (
                <p className="t-body text-n-900">
                  <span className="t-label block text-n-600">Z wykonania</span>
                  {montaz.uwagiWykonania}
                </p>
              )}
            </Karta>
          )}
            </>
          )}
        </div>

        <div className="min-w-0">
          <Karta tytul={`Trzy podpisy · ${podpisy.zlozone.length} z 3`}>
            {PODPISY.map((podpis) => {
              const zlozony = podpisy.zlozone.includes(podpis.kod);
              const kto = montaz[`${podpis.kod}By`];
              const kiedy = montaz[`${podpis.kod}At`];

              return (
                <div key={podpis.kod} className="mb-13 border-b border-n-100 pb-13 last:mb-0 last:border-b-0 last:pb-0">
                  <div className="t-body-strong text-n-900">{podpis.nazwa}</div>
                  <div className="t-small mb-9 text-n-600">{podpis.opis}</div>

                  {zlozony ? (
                    <div className="flex flex-wrap items-center gap-9">
                      <span className="t-small text-n-900">{kto?.imieNazwisko}</span>
                      <span className="t-mono-data text-n-500">{dataICzas(kiedy)}</span>
                      {mozePodpisywac && !urzadzenie && (
                        <Przycisk
                          type="button"
                          onClick={() => void przelaczPodpis(podpis.kod, true)}
                          disabled={zajete !== null}
                        >
                          Cofnij
                        </Przycisk>
                      )}
                    </div>
                  ) : mozePodpisywac ? (
                    <Przycisk
                      type="button"
                      onClick={() => void przelaczPodpis(podpis.kod, false)}
                      disabled={zajete !== null}
                      ikona={<IkonaPodpis rozmiar={16} />}
                    >
                      {zajete === podpis.kod ? 'Zapisywanie…' : 'Podpisuję'}
                    </Przycisk>
                  ) : (
                    <span className="t-small text-n-500">brak podpisu</span>
                  )}
                </div>
              );
            })}

            <p className="t-small mt-13 text-n-500">
              Kto kliknie, ten podpisuje — znacznik czasu wpisuje się sam. Jedna osoba może
              złożyć wszystkie trzy podpisy.
            </p>
          </Karta>

          <Karta tytul="Karta urządzenia">
            {urzadzenie ? (
              <>
                <div className="t-body-strong text-n-900">
                  {urzadzenie.producent} {urzadzenie.model}
                </div>
                <div className="t-mono-data mb-13 text-n-600">
                  nr boczny {urzadzenie.sideNumber} · {urzadzenie.qrCode}
                </div>
                <Przycisk type="button" onClick={() => idz(`/urzadzenia/${urzadzenie.id}`)}>
                  Otwórz kartę
                </Przycisk>
              </>
            ) : (
              <p className="t-small text-n-600">
                {podpisy.komplet
                  ? 'Wszystkie podpisy są. Możesz założyć kartę urządzenia — podzespoły przejdą z druku, numer boczny i kod QR nada system.'
                  : `Karta powstanie po trzech podpisach. Brakuje: ${podpisy.brakujace
                      .map((r) => PODPISY.find((p) => p.kod === r)?.nazwa ?? r)
                      .join(', ')}.`}
              </p>
            )}
          </Karta>
        </div>
      </div>
    </>
  );
}

/**
 * Kategoria, producent i model nie są polami druku montażu — druk opisuje podzespoły,
 * nie klasyfikację maszyny. Pytamy o nie dopiero teraz, gdy maszyna stoi złożona
 * i tabliczka jest czytelna. Podpowiadamy, co się da (CLAUDE.md §1 zasada 8).
 */
function FormularzZakonczenia({
  karta,
  onGotowe,
  onAnuluj,
}: {
  karta: KartaMontazu;
  onGotowe: (karta: KartaMontazu) => void;
  onAnuluj: () => void;
}) {
  const operacja = useIdOperacji();
  const { montaz } = karta;

  const [kategorie, setKategorie] = useState<Kategoria[] | null>(null);
  const [categoryId, setCategoryId] = useState('');
  // Producent jest znany już z zamówienia — nie pytamy o niego drugi raz (zasada 8).
  const [producent, setProducent] = useState(montaz.producent ?? '');
  const [model, setModel] = useState(montaz.modelGlowicy ?? '');
  const [serialNumber, setSerialNumber] = useState(montaz.serialNumber ?? '');
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  useEffect(() => {
    api
      .kategorie()
      .then((wynik) => setKategorie(wynik.kategorie))
      .catch(() => setBlad('Nie udało się wczytać kategorii urządzeń.'));
  }, []);

  const gotowe = categoryId && producent.trim() && model.trim() && serialNumber.trim();

  async function wyslij(zdarzenie: FormEvent): Promise<void> {
    zdarzenie.preventDefault();
    if (!gotowe || zapis) return;

    setZapis(true);
    setBlad(null);
    try {
      const nowa = await api.zakonczMontaz(montaz.id, {
        clientOperationId: operacja.biezacy(),
        categoryId,
        producent: producent.trim(),
        model: model.trim(),
        serialNumber: serialNumber.trim(),
      });
      operacja.zakoncz();
      onGotowe(nowa);
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się założyć karty.');
      setZapis(false);
    }
  }

  return (
    <form onSubmit={wyslij} noValidate>
      <Karta
        tytul="Karta urządzenia — czego nie ma na druku"
        wyroznienie
        akcje={
          <span className="t-small text-n-600">
            podzespoły, numer boczny i kod QR przejdą automatycznie
          </span>
        }
      >
        <div className="grid gap-13 sm:grid-cols-2">
          <label className="block">
            <span className="t-label mb-5 block text-n-700">Kategoria</span>
            <select
              value={categoryId}
              onChange={(z) => setCategoryId(z.target.value)}
              className="w-full border border-n-300 bg-surface px-13 text-n-900"
            >
              <option value="">— wybierz —</option>
              {kategorie?.flatMap((korzen) => splaszcz(korzen, 0))}
            </select>
          </label>

          <PoleTekstowe
            etykieta="Numer fabryczny"
            podpowiedz="z tabliczki znamionowej"
            value={serialNumber}
            onChange={(z) => setSerialNumber(z.target.value)}
            autoComplete="off"
          />

          <PoleTekstowe
            etykieta="Producent"
            podpowiedz={montaz.producent ? 'ze zlecenia montażu' : undefined}
            value={producent}
            onChange={(z) => setProducent(z.target.value)}
            autoComplete="off"
          />

          <PoleTekstowe
            etykieta="Model"
            podpowiedz="podpowiedziany z modelu głowicy"
            value={model}
            onChange={(z) => setModel(z.target.value)}
            autoComplete="off"
          />
        </div>

        {blad && (
          <div className="mt-13">
            <Komunikat tresc={blad} />
          </div>
        )}

        <div className="mt-14 flex flex-wrap items-center gap-13">
          <PrzyciskMarki type="submit" disabled={!gotowe || zapis}>
            {zapis ? 'Zakładanie…' : 'Załóż kartę i zakończ montaż'}
          </PrzyciskMarki>
          <Przycisk type="button" onClick={onAnuluj} disabled={zapis}>
            Anuluj
          </Przycisk>
        </div>
      </Karta>
    </form>
  );
}

function splaszcz(kategoria: Kategoria, poziom: number): React.ReactElement[] {
  return [
    <option key={kategoria.id} value={kategoria.id}>
      {`${'  '.repeat(poziom)}${kategoria.nazwa}`}
    </option>,
    ...kategoria.dzieci.flatMap((dziecko) => splaszcz(dziecko, poziom + 1)),
  ];
}
